<template>
  <b-modal
    v-model="showModal"
    size="xl"
    id="stock-change-history-modal"
    :title="modalTitle"
    hide-footer
    cancel-variant="outline-secondary"
  >
    <div class="d-flex flex-wrap align-items-end mb-1 stock-change-history-filters">
      <b-form-group
        label="Search by keyword"
        label-for="stock-history-keyword"
        class="mb-1 mr-1"
      >
        <b-form-input
          id="stock-history-keyword"
          v-model="filters.keyword"
          placeholder="Product name, SKU or barcode"
          :disabled="isLoading"
        />
      </b-form-group>
      <b-form-group
        label="From"
        label-for="stock-history-date-from"
        class="mb-1 mr-1"
      >
        <b-form-datepicker
          id="stock-history-date-from"
          v-model="filters.dateFrom"
          :disabled="isLoading"
          reset-button
        />
      </b-form-group>
      <b-form-group
        label="To"
        label-for="stock-history-date-to"
        class="mb-1 mr-1"
      >
        <b-form-datepicker
          id="stock-history-date-to"
          v-model="filters.dateTo"
          :disabled="isLoading"
          reset-button
        />
      </b-form-group>
      <b-button
        variant="outline-primary"
        size="sm"
        class="mb-1"
        :disabled="isLoading || !hasActiveFilters"
        @click="resetFilters"
      >
        Reset
      </b-button>
      <b-spinner
        v-if="isLoading"
        small
        class="mb-1 ml-1"
        label="Loading..."
      />
    </div>

    <div class="stock-change-history-table">
      <vue-good-table
        mode="remote"
        :columns="columns"
        :rows="rows"
        :total-rows="totalRows"
        :is-loading.sync="isLoading"
        :sort-options="{
          enabled: true,
          initialSortBy: { field: 'date', type: 'desc' },
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
        @page-change="onPageChange"
        @sort-change="onSortChange"
      >
      <template #table-row="props">
        <span v-if="props.column.field === 'date'">
          <div class="stock-history-date-cell">
            <div>{{ formatDate(props.row.date) }}</div>
            <b-button
              variant="outline-primary"
              size="sm"
              class="stock-history-view-btn"
              aria-label="View inventory change details"
              @click.stop="openRowDetail(props.row)"
            >
              Details
            </b-button>
          </div>
        </span>
        <span v-else-if="props.column.field === 'product_image'">
          <div class="stock-history-product-thumb">
            <img
              :src="props.row.product_image"
              alt=""
              class="stock-history-product-img"
            >
          </div>
        </span>
        <span v-else-if="props.column.field === 'product_name'">
          <span v-if="props.row.product_id">[#{{ props.row.product_id }}]</span>{{ props.row.product_id ? ' ' : '' }}<DatatableUserText field="product_name" :value="props.row.product_name" />
        </span>
        <span v-else-if="props.column.field === 'type'">
          <div class="stock-history-type-cell">
            <span
              class="d-inline-flex align-items-center"
              :class="props.row.type === 'in' ? 'text-success' : 'text-danger'"
            >
              <feather-icon
                :icon="props.row.type === 'in' ? 'ArrowDownIcon' : 'ArrowUpIcon'"
                size="16"
                class="mr-25"
              />
              <span>{{ props.row.type === 'in' ? 'In' : 'Out' }}</span>
            </span>
            <div
              v-if="props.row.supplier_name"
              class="d-flex align-items-center mt-25 stock-history-type-supplier"
            >
              <div
                class="stock-history-supplier-thumb"
                :class="{ 'stock-history-supplier-thumb--empty': !props.row.supplier_image }"
              >
                <img
                  v-if="props.row.supplier_image"
                  :src="props.row.supplier_image"
                  alt=""
                  class="stock-history-supplier-img"
                >
              </div>
              <span class="ml-50"><DatatableUserText :value="props.row.supplier_name" /></span>
            </div>
          </div>
        </span>
        <span v-else-if="props.column.field === 'quantity'">
          {{ props.row.quantity }}
        </span>
        <span v-else-if="props.column.field === 'user'">
          <div
            v-if="props.row.user_name"
            class="d-flex flex-column align-items-center text-center"
          >
            <b-avatar
              :src="props.row.user_picture"
              size="40"
              variant="light-primary"
              class="stock-history-user-avatar"
            />
            <small class="mt-25"><DatatableUserText :value="props.row.user_name" tag="span" /></small>
          </div>
        </span>
        <span v-else-if="props.column.field === 'notes'">
          <DatatableUserText :value="props.row.notes || ''" />
        </span>
        <DatatableCellValue
          v-else
          :field="props.column.field"
          :value="props.formattedRow[props.column.field]"
        />
      </template>

      <template #pagination-bottom="props">
        <TablePagination
              :value="currentPage"
              :total-rows="totalRows"
              :per-page="pageLength"
              @input="(value) => props.pageChanged({ currentPage: value })"
              @per-page-change="onPerPageChange"
            />
      </template>
      </vue-good-table>
    </div>
  </b-modal>

  <b-modal
    id="stock-change-history-detail-modal"
    v-model="showDetailModal"
    title="Inventory change details"
    size="md"
    @hidden="detailRow = null"
  >
    <div
      v-if="detailRow"
      class="stock-history-detail"
    >
      <div class="stock-history-detail__row">
        <div class="stock-history-detail__label">Date</div>
        <div class="stock-history-detail__value">{{ formatDate(detailRow.date) }}</div>
      </div>
      <div class="stock-history-detail__row">
        <div class="stock-history-detail__label">Image</div>
        <div class="stock-history-detail__value">
          <div class="stock-history-product-thumb">
            <img
              :src="detailRow.product_image"
              alt=""
              class="stock-history-product-img"
            >
          </div>
        </div>
      </div>
      <div class="stock-history-detail__row">
        <div class="stock-history-detail__label">Name</div>
        <div class="stock-history-detail__value">
          <span v-if="detailRow.product_id">[#{{ detailRow.product_id }}]</span>{{ detailRow.product_id ? ' ' : '' }}{{ detailRow.product_name }}
        </div>
      </div>
      <div class="stock-history-detail__row">
        <div class="stock-history-detail__label">Type</div>
        <div class="stock-history-detail__value">
          <span
            class="d-inline-flex align-items-center"
            :class="detailRow.type === 'in' ? 'text-success' : 'text-danger'"
          >
            <feather-icon
              :icon="detailRow.type === 'in' ? 'ArrowDownIcon' : 'ArrowUpIcon'"
              size="16"
              class="mr-25"
            />
            <span>{{ detailRow.type === 'in' ? 'In' : 'Out' }}</span>
          </span>
          <div
            v-if="detailRow.supplier_name"
            class="d-flex align-items-center mt-50"
          >
            <div
              class="stock-history-supplier-thumb"
              :class="{ 'stock-history-supplier-thumb--empty': !detailRow.supplier_image }"
            >
              <img
                v-if="detailRow.supplier_image"
                :src="detailRow.supplier_image"
                alt=""
                class="stock-history-supplier-img"
              >
            </div>
            <span class="ml-50">{{ detailRow.supplier_name }}</span>
          </div>
        </div>
      </div>
      <div class="stock-history-detail__row">
        <div class="stock-history-detail__label">Quantity</div>
        <div class="stock-history-detail__value">{{ detailRow.quantity }}</div>
      </div>
      <div class="stock-history-detail__row">
        <div class="stock-history-detail__label">User</div>
        <div class="stock-history-detail__value">
          <div
            v-if="detailRow.user_name"
            class="d-flex align-items-center"
          >
            <b-avatar
              :src="detailRow.user_picture"
              size="40"
              variant="light-primary"
            />
            <span class="ml-50">{{ detailRow.user_name }}</span>
          </div>
          <span v-else>—</span>
        </div>
      </div>
      <div class="stock-history-detail__row">
        <div class="stock-history-detail__label">Details</div>
        <div class="stock-history-detail__value">{{ detailRow.notes || '—' }}</div>
      </div>
    </div>
    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        @click="showDetailModal = false"
      >
        Close
      </b-button>
    </template>
  </b-modal>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import TablePagination from '@/views/Components/Elements/TablePagination.vue'
import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'
import DatatableUserText from '@/views/Components/Elements/DatatableUserText.vue'
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'
import {
  BAvatar,
  BButton,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BModal,
  BPagination,
  BSpinner,
} from 'bootstrap-vue'
import { resolvePictureUrl } from '@/libs/pictureUrl'
import { normalizeImageUrl } from '@/libs/utils'
import { formatCompanyDateTime } from '@/libs/companyTime'
import { toastFailure } from '@/libs/toastification'
import { $themeConfig } from '@themeConfig'

const NO_IMAGE = $themeConfig.app.noUnitImage || '/static_images/no_image.png'

const props = defineProps({
  clickDate: {
    type: Date,
    default: null,
  },
  stockLocation: {
    type: Object,
    default: null,
  },
})

const store = useStore()

const showModal = ref(false)
const showDetailModal = ref(false)
const detailRow = ref(null)
const isLoading = ref(false)
const rows = ref([])
const totalRows = ref(0)
const currentPage = ref(1)
const pageLength = ref(25)

function onPerPageChange(n) {
  pageLength.value = n
  currentPage.value = 1
  loadHistory()
}

const sortBy = ref('date')
const sortDir = ref('desc')

const filters = reactive({
  keyword: '',
  dateFrom: null,
  dateTo: null,
})

let keywordTimer = null
let loadSeq = 0

const columns = [
  { label: 'Date', field: 'date' },
  { label: 'Image', field: 'product_image', sortable: false, width: '70px' },
  { label: 'Name', field: 'product_name', sortable: false },
  { label: 'Type', field: 'type' },
  { label: 'Quantity', field: 'quantity' },
  { label: 'User', field: 'user', sortable: false, thClass: 'text-center', tdClass: 'text-center' },
  { label: 'Details', field: 'notes', sortable: false },
]

const modalTitle = computed(() => {
  const name = props.stockLocation?.name
  return name ? `Inventory change history — ${name}` : 'Inventory change history'
})

const hasActiveFilters = computed(() => Boolean(
  String(filters.keyword || '').trim()
  || filters.dateFrom
  || filters.dateTo,
))

const productPictureSrc = (unit) => {
  const picture = unit?.picture
  if (Array.isArray(picture) && picture[0]?.src) {
    return resolvePictureUrl(picture[0].src, NO_IMAGE)
  }
  return NO_IMAGE
}

const mapRow = (item) => ({
  id: item.id,
  date: item.date,
  product_id: item.unit?.id ?? '',
  product_name: item.unit?.name ?? '',
  product_image: productPictureSrc(item.unit),
  type: item.type,
  quantity: item.quantity,
  supplier_name: item.supplier?.name || '',
  supplier_image: item.supplier?.image
    ? resolvePictureUrl(item.supplier.image, NO_IMAGE)
    : '',
  user_name: item.user?.name || '',
  user_picture: normalizeImageUrl(item.user?.picture),
  notes: item.notes || '',
})

const formatDate = (value) => formatCompanyDateTime(value)

const openRowDetail = (row) => {
  detailRow.value = row
  showDetailModal.value = true
}

const loadHistory = async () => {
  if (!props.stockLocation?.id) {
    isLoading.value = false
    return
  }

  const seq = ++loadSeq
  isLoading.value = true

  try {
    const response = await store.dispatch('stockChanges/loadStockChangeHistory', {
      stockLocationId: props.stockLocation.id,
      params: {
        keyword: String(filters.keyword || '').trim() || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        sort_by: sortBy.value,
        sort_dir: sortDir.value,
        page: currentPage.value,
        per_page: pageLength.value,
      },
    })

    if (seq !== loadSeq) {
      return
    }

    const list = Array.isArray(response?.data) ? response.data : []
    rows.value = list.map(mapRow)
    totalRows.value = Number(response?.meta?.total ?? list.length)
  } catch (error) {
    if (seq !== loadSeq) {
      return
    }
    rows.value = []
    totalRows.value = 0
    toastFailure('Could not load inventory change history.')
  } finally {
    if (seq === loadSeq) {
      isLoading.value = false
    }
  }
}

const resetFilters = () => {
  filters.keyword = ''
  filters.dateFrom = null
  filters.dateTo = null
  currentPage.value = 1
  loadHistory()
}

const onPageChange = (params) => {
  const page = Number(params?.currentPage || 1)
  currentPage.value = page
  loadHistory()
}

const onSortChange = (params) => {
  const sort = Array.isArray(params) ? params[0] : params
  if (!sort?.field) {
    return
  }
  const nextDir = sort.type === 'asc' ? 'asc' : 'desc'
  if (sortBy.value === sort.field && sortDir.value === nextDir && currentPage.value === 1) {
    // Still fetch — remote mode may have already set loading true.
    loadHistory()
    return
  }
  sortBy.value = sort.field
  sortDir.value = nextDir
  currentPage.value = 1
  loadHistory()
}

watch(
  () => props.clickDate,
  (value) => {
    if (!value || !props.stockLocation?.id) {
      return
    }
    filters.keyword = ''
    filters.dateFrom = null
    filters.dateTo = null
    currentPage.value = 1
    sortBy.value = 'date'
    sortDir.value = 'desc'
    showDetailModal.value = false
    detailRow.value = null
    showModal.value = true
    loadHistory()
  },
)

watch(
  () => filters.keyword,
  () => {
    if (!showModal.value) {
      return
    }
    clearTimeout(keywordTimer)
    keywordTimer = setTimeout(() => {
      currentPage.value = 1
      loadHistory()
    }, 300)
  },
)

watch(
  () => [filters.dateFrom, filters.dateTo],
  () => {
    if (!showModal.value) {
      return
    }
    currentPage.value = 1
    loadHistory()
  },
)
</script>

<style lang="scss" scoped>
.stock-change-history-filters :deep(.form-group) {
  min-width: 180px;
}

.stock-history-date-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
}

.stock-history-view-btn {
  display: inline-flex;
  margin-top: 0.15rem;
}

@media (min-width: 992px) {
  .stock-history-view-btn {
    display: none;
  }
}

.stock-history-product-thumb {
  width: 48px;
  height: 48px;
  border: 1px solid #ddd;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.stock-history-product-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.stock-history-type-supplier {
  max-width: 160px;
}

.stock-history-supplier-thumb {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #fafafa;
}

.stock-history-supplier-thumb--empty {
  background: #f0f0f0;
}

.stock-history-supplier-img {
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
}

.stock-history-user-avatar {
  margin-left: auto;
  margin-right: auto;
}

.stock-history-detail__row {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid #dae1e7;
}

.stock-history-detail__row:last-of-type {
  border-bottom: 0;
}

.stock-history-detail__label {
  font-size: 0.75rem;
  color: #6e6b7b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.stock-history-detail__value {
  font-size: 0.95rem;
  color: #5e5873;
  word-break: break-word;
}

/*
 * Keep Date + Image; progressively hide later columns as the viewport narrows.
 * Columns: 1 Date, 2 Image, 3 Name, 4 Type, 5 Quantity, 6 User, 7 Details
 */
@mixin hide-from($start) {
  :deep(thead > tr > th:nth-child(n + #{$start})),
  :deep(tbody > tr > td:nth-child(n + #{$start})) {
    display: none !important;
  }
}

.stock-change-history-table {
  @media (max-width: 991.98px) {
    @include hide-from(6);
  }

  @media (max-width: 767.98px) {
    @include hide-from(3);
  }
}
</style>
